import React, { useState, useRef } from "react";
import {
  DocumentType,
  DOCUMENT_TYPES,
  InputPanelState,
  UploadedFileInfo
} from "../types";
import { getFilePreviewUrl, getFilePreviewPdf } from "../services/treatmentPlanSessions";
import ExcelPreview from "./ExcelPreview";
import "./UploadDocumentsStep.css";

interface UploadDocumentsStepProps {
  sessionId: number | null;
  activeDocumentType: DocumentType;
  uploadedFiles: Record<string, UploadedFileInfo>;
  inputStateMap: Record<DocumentType, InputPanelState>;
  pendingClears: Set<DocumentType>;
  onInputStateChange: (documentType: DocumentType, newState: InputPanelState) => void;
  onFileUpload: (documentType: DocumentType, file: File) => void;
  onFileClear: (documentType: DocumentType) => void;
}

const UploadDocumentsStep: React.FC<UploadDocumentsStepProps> = ({
  sessionId,
  activeDocumentType,
  uploadedFiles,
  inputStateMap,
  pendingClears,
  onInputStateChange,
  onFileUpload,
  onFileClear,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const loadIdRef = useRef(0);

  const isExcelType = (docType: DocumentType) => {
    return docType === DocumentType.VB_MAPP || docType === DocumentType.QABF;
  };

  const getAcceptedFileTypes = () => {
    if (isExcelType(activeDocumentType)) {
      return ".xlsx,.xls";
    }
    return ".pdf";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isExcel = isExcelType(activeDocumentType);
    const validPdf = !isExcel && file.type === "application/pdf";
    const validExcel = isExcel && (
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls")
    );

    if (!validPdf && !validExcel) {
      alert(isExcel ? "Please select an Excel file (.xlsx or .xls)" : "Please select a PDF file");
      return;
    }

    onFileUpload(activeDocumentType, file);
    onInputStateChange(activeDocumentType, {
      ...inputStateMap[activeDocumentType],
      file: file,
      showPreview: true,
    });

    // Excel files are rendered by ExcelPreview directly; only create blob URL for PDFs
    if (!isExcelType(activeDocumentType)) {
      revokePreview();
      setPreviewUrlTracked(URL.createObjectURL(file));
    }
  };

  const handleClear = () => {
    onFileClear(activeDocumentType);
    onInputStateChange(activeDocumentType, {
      ...inputStateMap[activeDocumentType],
      file: null,
      showPreview: false,
    });
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const revokePreview = () => {
    if (previewUrlRef.current && previewUrlRef.current.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = null;
    setPreviewUrl(null);
  };

  const setPreviewUrlTracked = (url: string) => {
    previewUrlRef.current = url;
    setPreviewUrl(url);
  };

  const loadPreview = async () => {
    revokePreview();

    const thisLoadId = ++loadIdRef.current;
    const inputState = inputStateMap[activeDocumentType];
    const uploadedFileInfo = isCleared ? undefined : uploadedFiles[activeDocumentType];
    const file = inputState?.file;

    if (!file && !uploadedFileInfo) return;

    // Local Excel files are rendered by ExcelPreview directly; no blob URL needed
    if (file && isExcelType(activeDocumentType)) return;

    setPreviewLoading(true);

    try {
      if (file) {
        if (thisLoadId !== loadIdRef.current) return;
        setPreviewUrlTracked(URL.createObjectURL(file));
      } else if (uploadedFileInfo && sessionId) {
        if (isExcelType(activeDocumentType)) {
          const response = await getFilePreviewPdf(sessionId, activeDocumentType);
          if (thisLoadId !== loadIdRef.current) return;
          if (response.pdf_data) {
            const binaryString = atob(response.pdf_data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: "application/pdf" });
            setPreviewUrlTracked(URL.createObjectURL(blob));
          }
        } else {
          const response = await getFilePreviewUrl(sessionId, activeDocumentType);
          if (thisLoadId !== loadIdRef.current) return;
          setPreviewUrlTracked(response.url);
        }
      }
    } catch (error) {
      if (thisLoadId !== loadIdRef.current) return;
      console.error("Failed to load preview:", error);
      setPreviewUrl(null);
    } finally {
      if (thisLoadId === loadIdRef.current) {
        setPreviewLoading(false);
      }
    }
  };

  const currentLocalFile = inputStateMap[activeDocumentType]?.file;
  const isCleared = pendingClears.has(activeDocumentType) && !currentLocalFile;
  const effectiveUploadedFile = isCleared ? undefined : uploadedFiles[activeDocumentType];

  React.useEffect(() => {
    loadPreview();
    return () => revokePreview();
  }, [activeDocumentType, currentLocalFile, effectiveUploadedFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFile = !!(currentLocalFile || effectiveUploadedFile);
  const fileName = currentLocalFile?.name || effectiveUploadedFile?.filename;
  const docTypeInfo = DOCUMENT_TYPES.find((dt) => dt.type === activeDocumentType);

  return (
    <div className="upload-documents-step">
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptedFileTypes()}
        onChange={handleFileChange}
        style={{ display: "none" }}
        id="file-input"
      />

      {previewLoading ? (
        <div className="upload-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      ) : hasFile ? (
        <div className="upload-preview">
          <div className="upload-preview-header">
            <div className="preview-file-info">
              <span className="preview-file-icon">📄</span>
              <span className="preview-file-name">{fileName}</span>
            </div>
            <div className="preview-actions">
              <button
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Change
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={handleClear}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="upload-preview-content">
            {inputStateMap[activeDocumentType]?.file && isExcelType(activeDocumentType) ? (
              <ExcelPreview
                file={inputStateMap[activeDocumentType]?.file!}
                showPreview={true}
                autoLoadOnMount={true}
                containerClassName="upload-excel-preview"
              />
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                title="Document Preview"
                className="preview-iframe"
              />
            ) : (
              <div className="upload-preview-unavailable">
                <span className="preview-unavailable-icon">📄</span>
                <p>Preview not available</p>
                <button className="btn btn-secondary btn-sm" onClick={loadPreview}>
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <label htmlFor="file-input" className="upload-placeholder">
          <div className="placeholder-content">
            <span className="placeholder-icon">{docTypeInfo?.icon || "📄"}</span>
            <h3>{docTypeInfo?.name}</h3>
            <p className="placeholder-description">{docTypeInfo?.description}</p>
            <p className="placeholder-hint">
              Click to select a {isExcelType(activeDocumentType) ? "Excel (.xlsx, .xls)" : "PDF"} file
            </p>
          </div>
        </label>
      )}
    </div>
  );
};

export default UploadDocumentsStep;
